import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Select, MenuItem, Chip, IconButton, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ROLES, DEFAULT_ROLE, roleColor } from './permissionsConfig';
import { CATALOG_PARENTS, descendantsOf } from './permissionCatalog';
import RolesManagerDialog from './RolesManagerDialog';

// Admin-restriction framework. A single `can(key)` drives all gating —
// screens, sub-tabs AND individual fields (the key namespace already supports
// `<area>.field.<x>`, so adding a field exclusion later = add a key + wrap the
// field in `can()`; nothing else changes).
//
// Role definitions are editable by a super-user via the manager dialog and kept
// in localStorage for now (interim, per-browser) until a backend per-user model
// lands. Until a real role is assigned, every user is DEFAULT_ROLE (full
// access), so production behaviour is unchanged; gating only bites while a
// super-user simulates a restricted role.

const STORAGE_KEY = 'sft_role_defs_v3';

// Persisted shape: the non-owner roles as { key: { label, allow:[...] } }.
// Owner is always code-defined ('*') — never stored, never editable.
const loadStoredRoles = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : null;
    } catch (e) {
        return null;
    }
};

const buildRoleDefs = (stored) => {
    const nonOwner = (stored && Object.keys(stored).length)
        ? stored
        : {
            manager: { label: ROLES.manager.label, allow: ROLES.manager.allow },
            editor:  { label: ROLES.editor.label,  allow: ROLES.editor.allow },
        };
    return { owner: { label: ROLES.owner.label, allow: '*' }, ...nonOwner };
};

const persistRoles = (defs) => {
    const nonOwner = {};
    Object.entries(defs).forEach(([k, d]) => { if (d.allow !== '*') nonOwner[k] = { label: d.label, allow: d.allow }; });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(nonOwner)); } catch (e) { /* ignore */ }
};

const newRoleKey = () => 'role_' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36);

const PermissionsContext = createContext({
    can: () => true,
    role: DEFAULT_ROLE,
    simulatedRole: null,
    isSimulating: false,
    setSimulatedRole: () => {},
    roleDefs: ROLES,
    updateRoleAllow: () => {},
    addRole: () => {},
    renameRole: () => {},
    deleteRole: () => {},
    duplicateRole: () => {},
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ showSimulator = false, t, muiTheme, children }) => {
    // null = use the real (assigned) role; a role key = preview that role.
    const [simulatedRole, setSimulatedRole] = useState(null);
    const [roleDefs, setRoleDefs] = useState(() => buildRoleDefs(loadStoredRoles()));
    const [managerOpen, setManagerOpen] = useState(false);

    // Real role for now: everyone is full-access (DEFAULT_ROLE) until the
    // backend per-user model lands. Swap this for the user's assigned role then.
    const realRole = DEFAULT_ROLE;
    const effectiveRole = (simulatedRole && roleDefs[simulatedRole]) ? simulatedRole : realRole;

    const can = useMemo(() => {
        const def = roleDefs[effectiveRole] || roleDefs[DEFAULT_ROLE];
        const allow = def ? def.allow : '*';
        if (allow === '*') return () => true;
        const set = new Set(Array.isArray(allow) ? allow : []);
        return (key) => {
            if (set.has(key)) return true;
            // Unknown key (not in the catalog) → fail-open: never hides UI.
            if (!(key in CATALOG_PARENTS)) return true;
            // A container stays visible if any descendant is granted, so a
            // partially-granted screen/tab remains navigable.
            return descendantsOf(key).some(d => set.has(d));
        };
    }, [roleDefs, effectiveRole]);

    // All mutations persist immediately. Functional updates → stable handlers
    // (empty deps), so the context value only changes when roleDefs really does.
    const updateRoleAllow = useCallback((key, allow) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev, [key]: { ...prev[key], allow } };
            persistRoles(next);
            return next;
        });
    }, []);
    const addRole = useCallback((label) => {
        const key = newRoleKey();
        setRoleDefs(prev => {
            const next = { ...prev, [key]: { label: label || 'Nieuwe rol', allow: [] } };
            persistRoles(next);
            return next;
        });
        return key;
    }, []);
    const duplicateRole = useCallback((key) => {
        const nk = newRoleKey();
        setRoleDefs(prev => {
            const src = prev[key];
            if (!src || src.allow === '*') return prev;
            const next = { ...prev, [nk]: { label: `${src.label || 'Rol'} (kopie)`, allow: Array.isArray(src.allow) ? [...src.allow] : [] } };
            persistRoles(next);
            return next;
        });
        return nk;
    }, []);
    const renameRole = useCallback((key, label) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev, [key]: { ...prev[key], label } };
            persistRoles(next);
            return next;
        });
    }, []);
    const deleteRole = useCallback((key) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev };
            delete next[key];
            persistRoles(next);
            return next;
        });
        setSimulatedRole(cur => (cur === key ? null : cur));
    }, []);

    const value = useMemo(() => ({
        can,
        role: effectiveRole,
        simulatedRole,
        isSimulating: !!simulatedRole,
        setSimulatedRole,
        roleDefs,
        updateRoleAllow,
        addRole,
        renameRole,
        deleteRole,
        duplicateRole,
    }), [can, effectiveRole, simulatedRole, roleDefs, updateRoleAllow, addRole, renameRole, deleteRole, duplicateRole]);

    // The simulator widget + manager dialog live above the app's own
    // ThemeProvider, so re-apply the active MUI theme here — otherwise they'd
    // render with MUI's default light theme even in dark mode.
    const overlay = showSimulator ? (
        <>
            {/* Hide the floating widget while the manager dialog is open — it
                sits above the dialog (zIndex 1400 > modal 1300) otherwise. */}
            {!managerOpen && (
                <RoleSimulator
                    simulatedRole={simulatedRole}
                    effectiveRole={effectiveRole}
                    roleDefs={roleDefs}
                    onChange={setSimulatedRole}
                    onOpenManager={() => setManagerOpen(true)}
                />
            )}
            <RolesManagerDialog
                open={managerOpen}
                onClose={() => setManagerOpen(false)}
                roleDefs={roleDefs}
                onChangeRole={updateRoleAllow}
                onAddRole={addRole}
                onRenameRole={renameRole}
                onDeleteRole={deleteRole}
                onDuplicateRole={duplicateRole}
                t={t}
            />
        </>
    ) : null;

    return (
        <PermissionsContext.Provider value={value}>
            {children}
            {overlay && (muiTheme ? <ThemeProvider theme={muiTheme}>{overlay}</ThemeProvider> : overlay)}
        </PermissionsContext.Provider>
    );
};

PermissionsProvider.propTypes = {
    showSimulator: PropTypes.bool,
    t: PropTypes.func,
    muiTheme: PropTypes.object,
    children: PropTypes.node,
};

// Floating dev/test widget (super-users, webshop screens only) to preview the
// admin as a role and to open the role manager. Collapsible: a small launcher
// pill (bottom-right, above the config save FAB and clear of the left sidebar)
// that reveals the panel on hover/click; collapse via the chevron.
const RoleSimulator = ({ simulatedRole, effectiveRole, roleDefs, onChange, onOpenManager }) => {
    const active = !!simulatedRole;
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ position: 'fixed', right: 24, bottom: 88, zIndex: 1400, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {open ? (
                <Paper
                    elevation={6}
                    sx={{
                        px: 1.5, py: 1, borderRadius: 2, width: 280,
                        border: active ? '2px solid' : '1px solid',
                        borderColor: active ? 'warning.main' : 'divider',
                        backgroundColor: active ? 'rgba(237,108,2,0.06)' : 'background.paper',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <VisibilityIcon fontSize="small" color={active ? 'warning' : 'disabled'} />
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, flex: 1 }}>
                            Simuleer rol
                        </Typography>
                        {active && <Chip size="small" color="warning" label="actief" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        <Tooltip title="Rollen beheren">
                            <IconButton size="small" onClick={onOpenManager}>
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Inklappen">
                            <IconButton size="small" onClick={() => setOpen(false)}>
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Select
                        size="small"
                        fullWidth
                        value={simulatedRole || ''}
                        displayEmpty
                        onChange={(e) => onChange(e.target.value || null)}
                        sx={{ fontSize: '0.85rem' }}
                        MenuProps={{ sx: { zIndex: 1500 } }}
                    >
                        <MenuItem value=""><em>Echte rol (volledige toegang)</em></MenuItem>
                        {Object.entries(roleDefs).map(([key, def]) => (
                            <MenuItem key={key} value={key}>
                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: roleColor(key), mr: 1, flexShrink: 0 }} />
                                {def.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {active && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.3 }}>
                            Je bekijkt de admin als <b>{roleDefs[effectiveRole]?.label || effectiveRole}</b>. Enkel een voorvertoning — je eigen rechten wijzigen niet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper
                    elevation={6}
                    onClick={() => setOpen(true)}
                    onMouseEnter={() => setOpen(true)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1.25, py: 0.5, borderRadius: 5, cursor: 'pointer',
                        border: active ? '2px solid' : '1px solid',
                        borderColor: active ? 'warning.main' : 'divider',
                        backgroundColor: active ? 'rgba(237,108,2,0.10)' : 'background.paper',
                        '&:hover': { boxShadow: 4 },
                    }}
                >
                    <VisibilityIcon fontSize="small" color={active ? 'warning' : 'action'} />
                    {active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: roleColor(effectiveRole), flexShrink: 0 }} />}
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {active ? (roleDefs[effectiveRole]?.label || 'Rol') : 'Simuleer rol'}
                    </Typography>
                    <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </Paper>
            )}
        </Box>
    );
};

RoleSimulator.propTypes = {
    simulatedRole: PropTypes.string,
    effectiveRole: PropTypes.string,
    roleDefs: PropTypes.object,
    onChange: PropTypes.func,
    onOpenManager: PropTypes.func,
};
